import { metaEndpoints } from "@/shared/api/endpoints";
import { getConfig } from "@config/env";

const getCookie = async (name: string): Promise<string | null> => {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? (parts.pop()?.split(";").shift() ?? null) : null;
};

const fetchJsonWithTimeout = async (url: string, timeoutMs: number): Promise<unknown> => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { method: "GET", signal: controller.signal });
    if (!res.ok) {
      throw new Error(`Non-200 response: ${res.status}`);
    }
    return await res.json();
  } finally {
    clearTimeout(t);
  }
};

// Helper function to get user IP (mirrors legacy fallback list)
const getUserIp = async (): Promise<string> => {
  const sources = [
    "https://api64.ipify.org?format=json",
    "https://api.ipify.org?format=json",
    "https://httpbin.org/ip",
  ];

  for (const url of sources) {
    try {
      const data = await fetchJsonWithTimeout(url, 2500);
      if (url.includes("ipify")) {
        const ip = (data as { ip?: unknown } | null)?.ip;
        return typeof ip === "string" ? ip : "";
      }
      if (url.includes("httpbin")) {
        const origin = (data as { origin?: unknown } | null)?.origin;
        return typeof origin === "string" ? origin : "";
      }
    } catch (err) {
      console.warn(`Failed to fetch IP from ${url}`, err);
    }
  }

  return "";
};

/**
 * Legacy-compatible Meta conversion event helper.
 *
 * Mirrors old Vue `trackEvent(eventName, eventId)` behavior:
 * - Generates unique eventID suffix
 * - Fires client-side Meta Pixel event with eventID
 * - Forwards the same event to backend `/api/auth/conversions/meta` (Meta CAPI proxy)
 */
export const trackMetaEvent = async (
  eventName: string,
  eventIdPrefix: string
): Promise<void> => {
  if (typeof window === "undefined") return;

  const eventId =
    `${eventIdPrefix}${Date.now()}_` +
    Math.random().toString(36).substr(2);

  // Client-side tracking (Pixel)
  try {
    window.fbq?.(
      "track",
      eventName,
      { currency: "GBP", value: 1.0 },
      { eventID: eventId }
    );
  } catch (err) {
    console.warn("Meta Pixel tracking failed", err);
  }

  // Server-side tracking (CAPI) via backend proxy
  const fbp = await getCookie("_fbp");
  const fbc = await getCookie("_fbc");
  const eventTime = Date.now();

  const userData: Record<string, unknown> = {
    fbp,
    client_ip_address: await getUserIp(),
    client_user_agent: navigator.userAgent,
  };
  if (fbc) {
    userData.fbc = fbc;
  }

  // Keep the exact nested payload shape used by legacy
  const payload = {
    data: {
      data: [
        {
          event_name: eventName,
          event_time: eventTime,
          event_id: eventId,
          event_source_url: window.location.href,
          action_source: "website",
          user_data: userData,
          custom_data: {
            content_name: eventName,
            value: 1.0,
            currency: "GBP",
          },
          original_event_data: {
            event_name: eventName,
            event_time: eventTime,
          },
        },
      ],
    },
  };

  try {
    // IMPORTANT: do NOT send this via our Axios client.
    // The Axios interceptor treats any 401 as session-expired and can force logout.
    // Legacy app used a bare fetch with no auth header; mirror that behavior.
    const config = getConfig();
    const base = (config.api.baseURL || "").trim().replace(/\/+$/, "");
    const url = base ? `${base}${metaEndpoints.metaConversions}` : metaEndpoints.metaConversions;

    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 10000);
    try {
      const token = (() => {
        try {
          return localStorage.getItem("authToken");
        } catch {
          return null;
        }
      })();

      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(t);
    }
  } catch (err) {
    console.warn("Meta conversions forward failed", err);
  }
};




